import { useState } from 'react'

import { show } from '@intercom/messenger-js-sdk'
import { Form, message } from 'antd'
import styled from 'styled-components'

import { Button } from '../../shared-components/BUI/Button'
import { Card } from '../../shared-components/BUI/Card'

import { FeedbackForm, FeedbackFormValues } from './FeedbackForm'
import { SuccessContent } from './SuccessContent'
import { TypeSelection } from './TypeSelection'

import { useProject } from '@/context/projectContext'
import { useUser } from '@/context/userContext'
import { useGatherFeedback } from '@/hooks/useGatherFeedback'
import { useSubmitFeedback } from '@/hooks/useSubmitFeedback'

export type FeedbackType =
  | 'choose'
  | 'issue'
  | 'idea'
  | 'other'
  | 'rating'
  | 'success'

interface FeedbackProps {
  onClose: () => void
  defaultType?: FeedbackType
  feedbackTitle?: string
  feedbackSubtitle?: string
  placeholderOverride?: string
}

const FeedbackSectionWrapper = styled(Card).attrs({
  bordered: false,
  'data-html2canvas-ignore': true,
})`
  border: none;
  width: 100%;

  .ant-card-body {
    padding: 0rem;
  }

  ${({ theme: { media } }) => media.lg`
    width: 400px;

    .ant-card-body {
      padding: 1.5rem;
    }
  `}
`

export const FeedbackSection = ({
  onClose,
  defaultType = 'choose',
  feedbackTitle,
  placeholderOverride,
}: FeedbackProps) => {
  const [form] = Form.useForm<FeedbackFormValues>()
  const [feedbackType, setFeedbackType] = useState<FeedbackType>(defaultType)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const { user } = useUser()

  const { project } = useProject()
  const { getFeedbackInfo } = useGatherFeedback()
  const { submitFeedback } = useSubmitFeedback()

  const handleSubmit = async (values: FeedbackFormValues) => {
    try {
      setIsSubmitting(true)
      const metaFeedback = getFeedbackInfo()

      const stillUploading = values.attachments?.some(
        (a) => a.status === 'uploading'
      )

      if (stillUploading) {
        message.error('Please wait until all files have finished uploading')
        return
      }

      await submitFeedback({
        type: feedbackType,
        message: values.message,
        attachments: values.attachments?.map((file) => ({
          url: file.url,
          name: file.name,
        })),
        metadata: metaFeedback,
        userId: user?.id,
      })

      setFeedbackType('success')
      form.resetFields()
    } catch {
      message.error('Failed to submit feedback')
    } finally {
      setIsSubmitting(false)
    }
  }

  const getSupport = () => {
    show()
    onClose()
  }

  const handleBack = () => {
    form.resetFields()
    setFeedbackType('choose')
  }

  const handleClose = () => {
    form.resetFields()
    setFeedbackType('choose')
    onClose()
  }

  const renderContent = () => {
    switch (feedbackType) {
      case 'rating':
        return (
          <FeedbackForm
            projectId={project?.id}
            title={feedbackTitle}
            isSubmitting={isSubmitting}
            textPlaceHolder="How can we improve the experience?"
            onClose={handleClose}
            showRating
          />
        )
      case 'idea':
        return (
          <FeedbackForm
            projectId={project?.id}
            title="Suggest an idea"
            textPlaceHolder={placeholderOverride || 'I would love...'}
            icon="💡"
            isSubmitting={isSubmitting}
            onBack={handleBack}
            onClose={handleClose}
          />
        )
      case 'issue':
        return (
          <FeedbackForm
            projectId={project?.id}
            title="Report a problem"
            textPlaceHolder={placeholderOverride || 'I noticed that...'}
            subtitle={
              <span>
                Is this problem affecting you right now? Start a{' '}
                <Button variant="link" onClick={getSupport}>
                  live chat
                </Button>{' '}
                with our team - we&apos;re here to help.
              </span>
            }
            icon="⚠️"
            isSubmitting={isSubmitting}
            onBack={handleBack}
            onClose={handleClose}
          />
        )
      case 'other':
        return (
          <FeedbackForm
            projectId={project?.id}
            title="Share something else"
            textPlaceHolder={placeholderOverride || 'Just wanted to say...'}
            icon="💬"
            isSubmitting={isSubmitting}
            onBack={handleBack}
            onClose={handleClose}
          />
        )
      case 'success':
        return (
          <SuccessContent onClose={handleClose} onSubmitAnother={handleBack} />
        )
      default:
        return (
          <TypeSelection
            onSelect={(type) => {
              setFeedbackType(type)
              form.resetFields()
            }}
            onClose={handleClose}
          />
        )
    }
  }

  return (
    <FeedbackSectionWrapper>
      <Form form={form} onFinish={handleSubmit}>
        {renderContent()}
      </Form>
    </FeedbackSectionWrapper>
  )
}
